// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Application Constants
 * Centralized configuration for API endpoints, colors, and other app-wide constants
 */

// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const API_PREFIX = '/api';

// Name of the Foundry agent or workflow the chat UI invokes. Sent to the BFF
// as `agent_or_workflow_name` on every chat request.
export const EMS_CHAT_AGENT_NAME =
  process.env.REACT_APP_EMS_CHAT_AGENT_NAME || 'helios-ems-chat-agent';

// HITL (Human-in-the-Loop) plan approvals.
// Single-site deployment today: HITL tracks sites by UUID, not the human
// label used elsewhere in the app. GA-10 is the demo/only site.
export const HITL_SITE_ID = '019e8469-25fe-7a59-93c3-afb2a7c00000';
export const HITL_DC_SITE_LABEL = 'GA-10';
export const HITL_APPROVAL_TYPE = 'PlanApproval_v1';

// Full API URL helper
export const getApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${cleanEndpoint}`;
};

// API Endpoints
export const ENDPOINTS = {
  // Me endpoints (current user resolved from JWT by downstream service)
  ME: '/api/v1/me',

  // User endpoints
  USERS: '/api/v1/users',
  INVITE_USER: '/api/v1/users/invite',
  USER_BY_ID: (userId: string) => `/api/v1/users/${userId}`,

  // Digital Twin endpoints
  DIGITAL_TWIN_DETAILS: (siteId: string) => `/api/v1/digital-twin/datacenters/details/${siteId}`,
  DIGITAL_TWIN_MODEL: '/api/v1/digital-twin/model',

  // AI Chat endpoints
  AI_CHAT: '/api/v1/ai/chat',
  AI_SESSIONS: '/api/v1/ai/sessions',
  AI_SESSION_MESSAGES: (sessionId: string) => `/api/v1/ai/sessions/${sessionId}/messages`,

  // AI Onboarding workflow
  ONBOARDING_RUN: '/api/v1/ai/onboarding/run',
  ONBOARDING_RESUME: '/api/v1/ai/onboarding/resume',

  // AI Equipment documentation Q&A
  EQUIPMENT_DOCS_ASK: '/api/v1/ai/equipment-docs/ask',

  // Ask Helios — advisory SOP Q&A (structured, non-streaming)
  AI_ASK_HELIOS: '/api/v1/ai/ask-helios',
  AI_ASK_HELIOS_STREAM: '/api/v1/ai/ask-helios/stream',

  // Audit Log endpoints
  AUDIT_LOGS: '/api/v1/audit-logs',
  AUDIT_LOGS_RECENT: '/api/v1/audit-logs/recent',

  // Admin endpoints (cross-organization for admin)
  ADMIN_ALL_USERS: '/api/v1/admin/all-users',
  ADMIN_AUDIT_LOGS: '/api/v1/admin/audit-logs',

  // Object Store endpoints
  OBJECT_STORE: '/api/v1/object-store',
  OBJECT_STORE_OBJECT: (key: string) => `/api/v1/object-store/objects/${key}`,
  OBJECT_STORE_DOWNLOAD: (key: string) => `/api/v1/object-store/download/${key}`,
  OBJECT_STORE_UPLOAD: (key: string) => `/api/v1/object-store/upload/${key}`,

  // Plan endpoints
  PLANS: '/api/v1/plans',

  // HITL (Human-in-the-Loop) endpoints
  HITL_APPROVALS: '/api/v1/hitl/approvals',
  HITL_REVIEW_DECISIONS: (reviewId: string) => `/api/v1/hitl/reviews/${reviewId}/decisions`,

  // OMS endpoints (Organization Management Service)
  OMS_ORGANIZATIONS: '/api/v1/oms/organizations',
  OMS_ORGANIZATION_BY_ID: (orgId: string) => `/api/v1/oms/organizations/${orgId}`,
  OMS_LEVELS: (orgId: string) => `/api/v1/oms/organizations/${orgId}/levels`,
  OMS_LEVEL_BY_ID: (orgId: string, levelId: string) =>
    `/api/v1/oms/organizations/${orgId}/levels/${levelId}`,
  OMS_REGIONS: (orgId: string) => `/api/v1/oms/organizations/${orgId}/regions`,
  OMS_REGION_BY_ID: (orgId: string, regionId: string) =>
    `/api/v1/oms/organizations/${orgId}/regions/${regionId}`,
  OMS_SITES: (orgId: string) => `/api/v1/oms/organizations/${orgId}/sites`,
  OMS_SITE_BY_ID: (orgId: string, siteId: string) =>
    `/api/v1/oms/organizations/${orgId}/sites/${siteId}`,

  // Alarm Event Management (AEM) API endpoints — scoped to a site
  ALARMS: (siteId: string) => `/api/v1/sites/${siteId}/alarms`,
  ALARM_BY_ID: (siteId: string, alarmCode: string) => `/api/v1/sites/${siteId}/alarms/${alarmCode}`,

  // SOP Factory Catalog API (read-only SOP/policy-as-code artifacts)
  SOP_FACTORY_ARTIFACTS: '/api/v1/sop-factory/artifacts',
  SOP_FACTORY_ARTIFACT_BY_ID: (id: string) => `/api/v1/sop-factory/artifacts/${id}`,

  // Telemetry API (observed/forecast equipment timeseries, via the BFF relay)
  TELEMETRY_EQUIPMENT_OBSERVATIONS: (siteId: string, equipmentId: string) =>
    `/api/v1/telemetry/sites/${siteId}/equipment/${equipmentId}/observations`,
};

// Site id the AEM alarms API is queried against. Single-site deployment today
// (GA-10, same UUID as HITL_SITE_ID). Shared by the alarms table and the alarm
// detail data path so a detail view can be resolved from just an alarm_code.
export const ALARM_API_SITE_ID = '019e8469-25fe-7a59-93c3-afb2a7c00000';

// Qcells Design System Colors
export const COLORS = {
  primary: '#00C6C1',
  secondary: '#0095D6',
  background: '#0a0e1a',
  cardBackground: '#fafafa',
  text: {
    primary: '#ffffff',
    secondary: '#a0a0a0',
    muted: '#666666',
  },
  status: {
    success: '#00C6C1',
    warning: '#FFA500',
    error: '#FF4444',
    info: '#0095D6',
  },
  border: '#2a2f3e',
};

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  OPERATIONS_MANAGER: 'operations_manager',
  OPERATOR: 'operator',
  EXECUTIVE: 'executive',
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DATETIME: 'MMM DD, YYYY HH:mm',
  TIME: 'HH:mm:ss',
};

// Local Storage Keys
// JWT tokens are managed by MSAL.js in its own localStorage keys.
// These keys are for app-level data only.
export const STORAGE_KEYS = {
  USER_DATA: 'helios_user_data',
  USER_PREFERENCES: 'helios_user_prefs',
};
